/**
 * Roles carried by a flow, wrapper or scheduler record (its identity_role rows).
 * Executions of the record run with these roles; a required role must be held
 * by whoever starts the record.
 */
export type RecordRole = { name: string; required: boolean; propagate: boolean };

export const getRecordRoles = (resourceData: any): RecordRole[] =>
    ((resourceData?.identity_role_plain_list_identity_id ?? []) as any[])
        .filter((relation) => relation?.role_id_role?.name)
        .map((relation) => ({
            name: relation.role_id_role.name as string,
            // The backend default for flow/wrapper/scheduler roles is required.
            required: relation.required !== false,
            propagate: !!relation.propagate,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

const joinNames = (roles: RecordRole[]) => roles.map((role) => role.name).join(", ");

/**
 * One-line description of the roles a record runs with, e.g. for the Run button
 * tooltip. Returns undefined when the record carries no roles.
 */
export const getRecordRolesNote = (
    roles: RecordRole[],
    recordLabel = "this flow",
): string | undefined => {
    if (roles.length === 0) return undefined;
    const required = roles.filter((role) => role.required);
    const granted = roles.filter((role) => !role.required);
    const parts = [`Runs with the roles of ${recordLabel}: ${joinNames(roles)}.`];
    if (required.length > 0) {
        parts.push(
            `You must hold ${required.length === 1 ? "the required role" : "the required roles"} ${joinNames(required)}.`,
        );
    }
    if (granted.length > 0) {
        parts.push(
            `${joinNames(granted)} ${granted.length === 1 ? "is" : "are"} given to everyone who runs it.`,
        );
    }
    return parts.join(" ");
};
